import { useState, useMemo } from 'react'
import { useTopAccounts } from '@/hooks/use-api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { ReportFilters } from '@/components/ReportFilters'
import type { ReportFilterValues } from '@/components/ReportFilters'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { Download, Filter, Building2, DollarSign, Users, TrendingUp, Loader2 } from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import { CHART_COLORS, SEMANTIC, CATEGORICAL } from '@/lib/theme'
import { exportToCSV } from '@/utils/csvExport'
import { ChartTooltipContent } from '@/components/ui/chart-tooltip'
import { explainError } from '@/lib/explain-error'

const COLORS = CHART_COLORS

export default function TopAccounts() {
  const [filters, setFilters] = useState<ReportFilterValues>({})
  const [limit, setLimit] = useState('25')
  const [sortBy, setSortBy] = useState('opportunity_revenue')
  const [appliedFilters, setAppliedFilters] = useState<{ limit?: number; sortBy?: string }>({
    limit: 25,
    sortBy: 'opportunity_revenue',
  })

  const { data, isLoading, error } = useTopAccounts({
    ...appliedFilters,
    ...(filters.owner_id ? { owner_id: filters.owner_id } : {}),
  })

  // Transform top accounts data for bar chart
  const topAccountsChartData = useMemo(() => {
    if (!data?.byOpportunityRevenue) return []
    return data.byOpportunityRevenue.slice(0, 10).map((item: any) => ({
      name: item.name?.length > 20 ? item.name.substring(0, 20) + '...' : item.name,
      revenue: parseFloat(item.closed_won_revenue || '0'),
      opportunities: item.opportunity_count,
    }))
  }, [data])

  // Transform concentration data for pie chart
  const concentrationChartData = useMemo(() => {
    if (!data?.concentration || data.concentration.length === 0) return []

    // Sum top 10 revenue
    const top10 = data.concentration.slice(0, 10)
    const top10Revenue = top10.reduce(
      (sum: number, item: any) => sum + parseFloat(item.revenue || '0'),
      0
    )

    // Calculate total from all accounts
    const totalRevenue = data.concentration.reduce(
      (sum: number, item: any) => sum + parseFloat(item.revenue || '0'),
      0
    )

    const otherRevenue = totalRevenue - top10Revenue

    return [
      { name: 'Top 10 Accounts', value: top10Revenue },
      { name: 'Other Accounts', value: otherRevenue },
    ]
  }, [data])

  // Calculate top 10 percentage for summary card
  const top10Percentage = useMemo(() => {
    if (!data?.concentration || data.concentration.length === 0) return 0
    const top10 = data.concentration.slice(0, 10)
    const top10Revenue = top10.reduce(
      (sum: number, item: any) => sum + parseFloat(item.revenue || '0'),
      0
    )
    const totalRevenue = data.concentration.reduce(
      (sum: number, item: any) => sum + parseFloat(item.revenue || '0'),
      0
    )
    return totalRevenue > 0 ? (top10Revenue / totalRevenue) * 100 : 0
  }, [data])

  const summary = data?.summary

  const handleApplyFilters = () => {
    setAppliedFilters({
      limit: parseInt(limit),
      sortBy,
    })
  }

  const handleClearFilters = () => {
    setLimit('25')
    setSortBy('opportunity_revenue')
    setAppliedFilters({ limit: 25, sortBy: 'opportunity_revenue' })
  }

  const handleExportCSV = () => {
    if (!data?.byOpportunityRevenue) return

    const exportData = data.byOpportunityRevenue.map((item: any) => {
      const closedWonRevenue = parseFloat(item.closed_won_revenue || '0')
      const wonCount = parseInt(item.won_count || '0')
      const avgDealSize = wonCount > 0 ? closedWonRevenue / wonCount : 0
      return {
        'Account Name': item.name,
        'Total Revenue (CAD)': closedWonRevenue,
        'Opportunity Count': item.opportunity_count,
        'Won Count': item.won_count,
        'Avg Deal Size (CAD)': avgDealSize,
        'Pipeline Value (CAD)': item.pipeline_value,
      }
    })

    exportToCSV({ data: exportData, filename: 'top-accounts.csv' })
  }

  // Check if we have data but it's empty
  const hasEmptyResults =
    !isLoading &&
    !error &&
    data &&
    (!data.byOpportunityRevenue || data.byOpportunityRevenue.length === 0)

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Top Accounts</h1>
          <p className="text-muted-foreground">Analyze highest-value customer accounts</p>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <p className="text-destructive font-medium">Failed to load top accounts data.</p>
              <p className="text-sm text-muted-foreground">
                Error: {explainError(error, 'Unknown error occurred')}
              </p>
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                aria-label="Reload page"
              >
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Top Accounts</h1>
          <p className="text-muted-foreground">Analyze highest-value customer accounts</p>
        </div>
        <div className="flex items-center gap-2">
          <ReportFilters
            value={filters}
            onChange={setFilters}
            defaultPeriod="all"
            showPeriod={false}
            datePresets={[
              'this_month',
              'this_quarter',
              'fiscal_year',
              'calendar_year',
              'last_12_months',
              'all',
              'custom',
            ]}
          />
          <Button
            onClick={handleExportCSV}
            variant="outline"
            disabled={isLoading || !data?.byOpportunityRevenue}
            aria-label="Export top accounts to CSV"
          >
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" aria-hidden="true" />
            Filters
          </CardTitle>
          <CardDescription>Configure account ranking parameters</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label htmlFor="limit">Number of Accounts</Label>
              <Select value={limit} onValueChange={setLimit}>
                <SelectTrigger id="limit" aria-label="Select number of accounts to display">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">Top 10</SelectItem>
                  <SelectItem value="25">Top 25</SelectItem>
                  <SelectItem value="50">Top 50</SelectItem>
                  <SelectItem value="100">Top 100</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="sortBy">Sort By</Label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger id="sortBy" aria-label="Select sorting criteria">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="opportunity_revenue">Opportunity Revenue</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={handleApplyFilters} className="flex-1">
                Apply Filters
              </Button>
              <Button onClick={handleClearFilters} variant="outline">
                Clear
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Empty State Warning */}
      {hasEmptyResults && (
        <Card
          style={{
            backgroundColor: SEMANTIC.warning.fill,
            borderColor: SEMANTIC.warning.fill,
          }}
        >
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="font-medium" style={{ color: SEMANTIC.warning.text }}>
                No account data found
              </p>
              <p className="text-sm" style={{ color: SEMANTIC.warning.text }}>
                No accounts with opportunities were found. Please check your data or adjust your
                filters.
              </p>
              <Button
                onClick={handleClearFilters}
                variant="outline"
                size="sm"
                className="mt-2"
                aria-label="Clear filters"
              >
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Statistics */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2
            className="h-8 w-8 animate-spin text-muted-foreground"
            aria-label="Loading top accounts data"
          />
        </div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                <CardTitle className="text-sm font-medium">Total Revenue (CAD)</CardTitle>
                <DollarSign
                  className="h-4 w-4"
                  aria-hidden="true"
                  style={{ color: CATEGORICAL.evergreen.solid }}
                />
              </CardHeader>
              <CardContent className="p-3">
                <div className="text-xl font-bold leading-none">
                  {formatCurrency(summary?.total_revenue || 0, 'CAD')}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">all accounts</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                <CardTitle className="text-sm font-medium">Top 10 %</CardTitle>
                <TrendingUp
                  className="h-4 w-4"
                  aria-hidden="true"
                  style={{ color: CATEGORICAL.teal.solid }}
                />
              </CardHeader>
              <CardContent className="p-3">
                <div className="text-xl font-bold leading-none">{top10Percentage.toFixed(1)}%</div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">
                  of total revenue
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                <CardTitle className="text-sm font-medium">Account Count</CardTitle>
                <Users
                  className="h-4 w-4"
                  aria-hidden="true"
                  style={{ color: CATEGORICAL.plum.solid }}
                />
              </CardHeader>
              <CardContent className="p-3">
                <div className="text-xl font-bold leading-none">
                  {summary?.total_accounts?.toLocaleString() || '0'}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">
                  with opportunities
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                <CardTitle className="text-sm font-medium">Avg per Account</CardTitle>
                <Building2
                  className="h-4 w-4"
                  aria-hidden="true"
                  style={{ color: CATEGORICAL.clay.solid }}
                />
              </CardHeader>
              <CardContent className="p-3">
                <div className="text-xl font-bold leading-none">
                  {formatCurrency(summary?.avg_revenue_per_account || 0, 'CAD')}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">
                  average revenue
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Top Accounts Bar Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Top 10 Accounts by Revenue</CardTitle>
                <CardDescription>Highest-value customer accounts</CardDescription>
              </CardHeader>
              <CardContent>
                {topAccountsChartData.length === 0 ? (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No account data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={topAccountsChartData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={150} />
                      <Tooltip
                        content={<ChartTooltipContent />}
                        formatter={(value: number, name: string) => {
                          if (name === 'Revenue') return formatCurrency(value, 'CAD')
                          return value
                        }}
                      />
                      <Legend />
                      <Bar dataKey="revenue" fill={COLORS[0]} name="Revenue" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Revenue Concentration Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Revenue Concentration</CardTitle>
                <CardDescription>Top 10 accounts vs rest of customer base</CardDescription>
              </CardHeader>
              <CardContent>
                {concentrationChartData.length === 0 ? (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No concentration data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={concentrationChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) =>
                          `${entry.name}: ${((entry.value / ((concentrationChartData[0]?.value ?? 0) + (concentrationChartData[1]?.value ?? 0))) * 100).toFixed(1)}%`
                        }
                        outerRadius={80}
                        fill={COLORS[0]}
                        dataKey="value"
                      >
                        {concentrationChartData.map((_entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        content={<ChartTooltipContent />}
                        formatter={(value: number) => formatCurrency(value, 'CAD')}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Detailed Table */}
          <Card>
            <CardHeader>
              <CardTitle>Account Rankings</CardTitle>
              <CardDescription>Complete list of top-performing accounts</CardDescription>
            </CardHeader>
            <CardContent>
              {!data?.byOpportunityRevenue || data.byOpportunityRevenue.length === 0 ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  No account data available
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table
                    className="w-full text-sm"
                    role="table"
                    aria-label="Top accounts detailed rankings"
                  >
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2 font-medium">Rank</th>
                        <th className="text-left p-2 font-medium">Account Name</th>
                        <th className="text-right p-2 font-medium">Total Revenue (CAD)</th>
                        <th className="text-right p-2 font-medium">Opportunities</th>
                        <th className="text-right p-2 font-medium">Won</th>
                        <th className="text-right p-2 font-medium">Avg Deal Size (CAD)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.byOpportunityRevenue.map((item: any, index: number) => {
                        const closedWonRevenue = parseFloat(item.closed_won_revenue || '0')
                        const wonCount = parseInt(item.won_count || '0')
                        const avgDealSize = wonCount > 0 ? closedWonRevenue / wonCount : 0
                        return (
                          <tr key={index} className="border-b hover:bg-muted/50">
                            <td className="p-2">{index + 1}</td>
                            <td className="p-2">{item.name}</td>
                            <td className="text-right p-2">
                              {formatCurrency(closedWonRevenue, 'CAD')}
                            </td>
                            <td className="text-right p-2">{item.opportunity_count}</td>
                            <td className="text-right p-2">{item.won_count}</td>
                            <td className="text-right p-2">{formatCurrency(avgDealSize, 'CAD')}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
